// Export des rapports (CSV, Excel, PDF), entièrement côté navigateur.
//
// Un export est décrit par :
//   - title       : titre du rapport ("Rapport des ventes")
//   - fileBaseName: base du nom de fichier ("rapport-ventes")
//   - filtersInfo : lignes décrivant les filtres appliqués
//   - summary     : indicateurs clés [{ label, value, type }]
//   - sections    : tableaux [{ title, columns: [{ label, value, type }], rows }]
//   - csvSection  : index de la section exportée en CSV (un CSV = un tableau)
//
// Types de colonnes / valeurs : "text" (défaut), "number", "currency",
// "percent" (valeur 0-100), "date", "datetime", "slot", "boolean".
// "slot" : date/heure d'un rendez-vous. L'API enregistre l'heure du salon
// telle quelle en UTC (09:00 réservé = 09:00Z) : on l'affiche donc en UTC,
// contrairement à "datetime" (horodatage réel affiché en heure locale).

import { getDateRangeFromPeriod } from "./reportUtils";

// Limite utilisée pour récupérer toutes les lignes d'une liste paginée
export const EXPORT_LIMIT = 10000;

export const PERIOD_LABELS = {
  today: "Aujourd'hui",
  week: "7 derniers jours",
  month: "30 derniers jours",
  quarter: "3 derniers mois",
  year: "12 derniers mois",
  custom: "Personnalisée",
};

const pad = (n) => String(n).padStart(2, "0");

const toDate = (value) => {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return isNaN(d.getTime()) ? null : d;
};

const formatDateFr = (value, withTime = false) => {
  const d = toDate(value);
  if (!d) return "";
  const date = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  return withTime ? `${date} ${pad(d.getHours())}:${pad(d.getMinutes())}` : date;
};

const formatSlotFr = (value) => {
  const d = toDate(value);
  if (!d) return "";
  return `${pad(d.getUTCDate())}/${pad(d.getUTCMonth() + 1)}/${d.getUTCFullYear()} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`;
};

// Séparateur de milliers en espace simple : les espaces insécables produits
// par toLocaleString("fr-FR") ne s'affichent pas avec les polices du PDF.
const formatNumberFr = (value, decimals = 0) => {
  const n = Number(value);
  if (!isFinite(n)) return "";
  const [int, dec] = Math.abs(n).toFixed(decimals).split(".");
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${n < 0 ? "-" : ""}${grouped}${dec ? `,${dec}` : ""}`;
};

/**
 * Plage de dates à transmettre aux listes de l'API, qui ne filtrent que par
 * startDate / endDate (le paramètre "period" n'y est pas pris en compte).
 */
export const getExportDateParams = (filters = {}) => {
  const { startDate, endDate } = getDateRangeFromPeriod(
    filters.period,
    filters.startDate,
    filters.endDate,
  );
  return { period: filters.period, startDate, endDate };
};

/**
 * Même plage, au format attendu par les listes de l'API. L'API convertit
 * "AAAA-MM-JJ" en minuit : sans heure explicite, la journée de fin (souvent
 * aujourd'hui) serait exclue de l'export.
 */
export const getExportApiDateParams = (filters = {}) => {
  const { period, startDate, endDate } = getExportDateParams(filters);
  return {
    period,
    startDate: startDate ? `${startDate}T00:00:00.000Z` : undefined,
    endDate: endDate ? `${endDate}T23:59:59.999Z` : undefined,
  };
};

/** Ligne de description de la période pour l'en-tête des exports. */
export const describePeriod = (filters = {}, period = null) => {
  const label = PERIOD_LABELS[filters.period] || "Toutes les dates";
  const start = period?.start || getExportDateParams(filters).startDate;
  const end = period?.end || getExportDateParams(filters).endDate;
  if (start && end) {
    return `Période : ${label} (du ${formatDateFr(start)} au ${formatDateFr(end)})`;
  }
  return `Période : ${label}`;
};

// ----------------------------------------------------------------------------
// Conversion des valeurs
// ----------------------------------------------------------------------------

const rawValue = (value, type) => {
  if (value === undefined || value === null || value === "") {
    return type === "number" || type === "currency" || type === "percent"
      ? 0
      : "";
  }
  switch (type) {
    case "number":
    case "currency":
    case "percent":
      return Number(value) || 0;
    case "date":
    case "datetime":
    case "slot":
      return toDate(value);
    case "boolean":
      return value ? "Oui" : "Non";
    default:
      return String(value);
  }
};

// Texte lisible (CSV et PDF)
const displayValue = (value, type) => {
  const v = rawValue(value, type);
  switch (type) {
    case "currency":
      return `${formatNumberFr(v)} FCFA`;
    case "number":
      return formatNumberFr(v, Number.isInteger(v) ? 0 : 2);
    case "percent":
      return `${formatNumberFr(v, 1)} %`;
    case "date":
      return formatDateFr(v);
    case "datetime":
      return formatDateFr(v, true);
    case "slot":
      return formatSlotFr(v);
    default:
      return v ?? "";
  }
};

// Valeur CSV : nombres bruts (virgule décimale) pour rester exploitables
const csvValue = (value, type) => {
  const v = rawValue(value, type);
  if (type === "number" || type === "currency" || type === "percent") {
    return String(Math.round(v * 100) / 100).replace(".", ",");
  }
  if (type === "date") return formatDateFr(v);
  if (type === "datetime") return formatDateFr(v, true);
  if (type === "slot") return formatSlotFr(v);
  return v ?? "";
};

const cellOf = (column, row) => {
  try {
    return column.value(row);
  } catch {
    return "";
  }
};

const timestamp = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

// ----------------------------------------------------------------------------
// CSV (séparateur ";" et BOM UTF-8 pour une ouverture correcte dans Excel FR)
// ----------------------------------------------------------------------------

const escapeCsv = (value) => {
  const s = String(value ?? "");
  return /[";\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const buildCsv = (config, options) => {
  const lines = [];
  const section = config.sections[config.csvSection || 0] || config.sections[0];

  if (options.includeFilters || options.includeSummary) {
    lines.push(escapeCsv(config.title));
    lines.push(escapeCsv(`Généré le ${formatDateFr(new Date(), true)}`));
  }
  if (options.includeFilters) {
    (config.filtersInfo || []).forEach((line) => lines.push(escapeCsv(line)));
  }
  if (options.includeSummary && config.summary?.length) {
    lines.push("");
    lines.push("Indicateur;Valeur");
    config.summary.forEach((kpi) =>
      lines.push(
        `${escapeCsv(kpi.label)};${escapeCsv(displayValue(kpi.value, kpi.type))}`,
      ),
    );
  }
  if (lines.length) lines.push("");

  if (section) {
    if (section.title && lines.length) lines.push(escapeCsv(section.title));
    lines.push(section.columns.map((c) => escapeCsv(c.label)).join(";"));
    section.rows.forEach((row) =>
      lines.push(
        section.columns
          .map((c) => escapeCsv(csvValue(cellOf(c, row), c.type)))
          .join(";"),
      ),
    );
  }

  return "﻿" + lines.join("\r\n");
};

// ----------------------------------------------------------------------------
// Excel (.xlsx réel, un onglet par tableau)
// ----------------------------------------------------------------------------

const EXCEL_FORMATS = {
  currency: '#,##0 "FCFA"',
  number: "#,##0.##",
  percent: '0.0" %"',
  date: "dd/mm/yyyy",
  datetime: "dd/mm/yyyy hh:mm",
  slot: "dd/mm/yyyy hh:mm",
};

// Excel affiche les dates en UTC : décaler les horodatages réels pour
// retrouver l'heure locale ; les créneaux ("slot") sont déjà en heure du salon.
const excelValue = (value, type) => {
  const v = rawValue(value, type);
  if ((type === "date" || type === "datetime") && v instanceof Date) {
    return new Date(v.getTime() - v.getTimezoneOffset() * 60000);
  }
  return v;
};

const sheetName = (name, used) => {
  let base = String(name || "Données")
    .replace(/[[\]:*?/\\]/g, " ")
    .slice(0, 31)
    .trim();
  let candidate = base || "Données";
  let i = 2;
  while (used.has(candidate)) {
    candidate = `${base.slice(0, 28)} ${i++}`;
  }
  used.add(candidate);
  return candidate;
};

const buildExcel = async (config, options) => {
  const ExcelJS = (await import("exceljs")).default;
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "Barberlys";
  workbook.created = new Date();
  const usedNames = new Set();
  const headerFill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFF3F4F6" },
  };

  if (options.includeFilters || options.includeSummary) {
    const sheet = workbook.addWorksheet(sheetName("Résumé", usedNames));
    sheet.columns = [{ width: 42 }, { width: 28 }];
    sheet.addRow([config.title]).font = { bold: true, size: 14 };
    sheet.addRow([`Généré le ${formatDateFr(new Date(), true)}`]);
    if (options.includeFilters) {
      (config.filtersInfo || []).forEach((line) => sheet.addRow([line]));
    }
    if (options.includeSummary && config.summary?.length) {
      sheet.addRow([]);
      const header = sheet.addRow(["Indicateur", "Valeur"]);
      header.font = { bold: true };
      header.eachCell((cell) => (cell.fill = headerFill));
      config.summary.forEach((kpi) => {
        const row = sheet.addRow([kpi.label, excelValue(kpi.value, kpi.type)]);
        if (EXCEL_FORMATS[kpi.type]) row.getCell(2).numFmt = EXCEL_FORMATS[kpi.type];
        row.getCell(2).alignment = { horizontal: "right" };
      });
    }
  }

  config.sections.forEach((section) => {
    const sheet = workbook.addWorksheet(sheetName(section.title, usedNames), {
      views: [{ state: "frozen", ySplit: 1 }],
    });
    sheet.columns = section.columns.map((c) => ({
      header: c.label,
      width: Math.max(12, Math.min(45, c.label.length + 6)),
    }));
    const header = sheet.getRow(1);
    header.font = { bold: true };
    header.eachCell((cell) => (cell.fill = headerFill));

    section.rows.forEach((row) => {
      sheet.addRow(section.columns.map((c) => excelValue(cellOf(c, row), c.type)));
    });

    section.columns.forEach((c, index) => {
      const column = sheet.getColumn(index + 1);
      if (EXCEL_FORMATS[c.type]) {
        column.eachCell({ includeEmpty: false }, (cell, rowNumber) => {
          if (rowNumber > 1) cell.numFmt = EXCEL_FORMATS[c.type];
        });
      }
      // Ajuster la largeur au contenu
      let max = c.label.length;
      section.rows.forEach((row) => {
        const len = String(displayValue(cellOf(c, row), c.type)).length;
        if (len > max) max = len;
      });
      column.width = Math.max(12, Math.min(50, max + 2));
    });

    if (section.rows.length) {
      sheet.autoFilter = {
        from: { row: 1, column: 1 },
        to: { row: 1, column: section.columns.length },
      };
    } else {
      sheet.addRow(["Aucune donnée"]);
    }
  });

  const buffer = await workbook.xlsx.writeBuffer();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
};

// ----------------------------------------------------------------------------
// PDF (A4 paysage, tableaux via jspdf-autotable)
// ----------------------------------------------------------------------------

const buildPdf = async (config, options) => {
  const { jsPDF } = await import("jspdf");
  const autoTable = (await import("jspdf-autotable")).default;

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
  const margin = 14;
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = margin;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text(config.title, margin, y + 4);
  y += 10;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(`Généré le ${formatDateFr(new Date(), true)} - Barberlys`, margin, y);
  y += 5;

  if (options.includeFilters) {
    (config.filtersInfo || []).forEach((line) => {
      doc.text(line, margin, y);
      y += 5;
    });
  }
  doc.setTextColor(0);

  const tableStyles = {
    styles: { fontSize: 8, cellPadding: 1.8 },
    headStyles: { fillColor: [31, 41, 55], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    margin: { left: margin, right: margin },
  };

  const sectionTitle = (title) => {
    if (y > doc.internal.pageSize.getHeight() - 30) {
      doc.addPage();
      y = margin;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(title, margin, y + 4);
    doc.setFont("helvetica", "normal");
    y += 7;
  };

  if (options.includeSummary && config.summary?.length) {
    sectionTitle("Indicateurs clés");
    autoTable(doc, {
      ...tableStyles,
      startY: y,
      head: [["Indicateur", "Valeur"]],
      body: config.summary.map((kpi) => [
        kpi.label,
        displayValue(kpi.value, kpi.type),
      ]),
      tableWidth: 140,
      columnStyles: { 1: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY + 8;
  }

  config.sections.forEach((section) => {
    sectionTitle(`${section.title} (${section.rows.length})`);
    const numericTypes = ["number", "currency", "percent"];
    autoTable(doc, {
      ...tableStyles,
      startY: y,
      head: [section.columns.map((c) => c.label)],
      body: section.rows.length
        ? section.rows.map((row) =>
            section.columns.map((c) => String(displayValue(cellOf(c, row), c.type))),
          )
        : [[{ content: "Aucune donnée", colSpan: section.columns.length }]],
      columnStyles: section.columns.reduce((acc, c, i) => {
        if (numericTypes.includes(c.type)) acc[i] = { halign: "right" };
        return acc;
      }, {}),
    });
    y = doc.lastAutoTable.finalY + 8;
  });

  // Numérotation des pages
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(
      `Page ${i}/${pageCount}`,
      pageWidth - margin,
      doc.internal.pageSize.getHeight() - 6,
      { align: "right" },
    );
  }

  return doc.output("blob");
};

// ----------------------------------------------------------------------------
// Point d'entrée
// ----------------------------------------------------------------------------

export const exportReport = async (config, format = "csv", options = {}) => {
  const opts = {
    includeFilters: options.includeFilters !== false,
    includeSummary: options.includeSummary !== false,
  };
  const base = `${config.fileBaseName}-${timestamp()}`;

  if (format === "excel") {
    triggerDownload(await buildExcel(config, opts), `${base}.xlsx`);
  } else if (format === "pdf") {
    triggerDownload(await buildPdf(config, opts), `${base}.pdf`);
  } else {
    const csv = buildCsv(config, opts);
    triggerDownload(new Blob([csv], { type: "text/csv;charset=utf-8;" }), `${base}.csv`);
  }
};
